const _isLocal = typeof location !== 'undefined' && (location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname.startsWith('192.168.') || location.hostname === 'dev-arp.fengbao.com' || location.hostname === '8.219.1.89')
const WS_URL = _isLocal
  ? 'wss://dev-arp-public-gateway.fengbao.com/ws'
  : 'wss://arp-public-gateway.fengbao.com/ws'
const HEARTBEAT_INTERVAL = 30000
const PONG_TIMEOUT = 5000
const BASE_RECONNECT_DELAY = 3000
const MAX_RECONNECT_DELAY = 60000

// 根据文件扩展名推断类型
const _AUDIO_EXTS = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'wma', 'm4a']
const _VIDEO_EXTS = ['mp4', 'webm', 'mkv', 'avi', 'mov']
const _IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
function _guessKindByExt(fileName) {
  const ext = (fileName || '').split('.').pop().toLowerCase()
  if (_AUDIO_EXTS.includes(ext)) return 'audio'
  if (_VIDEO_EXTS.includes(ext)) return 'video'
  if (_IMAGE_EXTS.includes(ext)) return 'image'
  return null
}

// 浏览器桌面通知
function _triggerBrowserNotification(store, { title, body, icon, conversationId }) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  const ns = store.state.notificationSettings
  if (!ns || !ns.notificationMessage) return
  // 勿扰模式检查
  if (ns.dndEnabled) {
    const now = new Date()
    const hm = String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0')
    const start = ns.dndStart || '22:00'
    const end = ns.dndEnd || '08:00'
    if (start > end) { // 跨午夜：22:00-08:00
      if (hm >= start || hm < end) return
    } else {
      if (hm >= start && hm < end) return
    }
  }
  // 只在页面不可见（最小化 / 切到别的 tab / 锁屏）时弹
  if (document.visibilityState === 'visible') return
  try {
    const n = new Notification(title, { body: body || '', icon: icon || '', tag: conversationId })
    n.onclick = () => {
      window.focus()
      // 找到对应 session 并切换
      const session = store.state.sessionItems.find(s => s.conversationId === conversationId)
      if (session) store.dispatch('setActiveSession', session.key)
      n.close()
    }
  } catch (e) { /* ignore */ }
}

function getToken() {
  try {
    const raw = localStorage.getItem('mcn_auth')
    const s = raw ? JSON.parse(raw) : null
    return s ? s.token : null
  } catch { return null }
}

function getDeviceId() {
  let id = localStorage.getItem('device_id')
  if (!id) {
    id = 'web-' + Math.random().toString(36).slice(2) + '-' + Date.now()
    localStorage.setItem('device_id', id)
  }
  return id
}

class WebSocketClient {
  constructor(store) {
    this.store = store
    this.ws = null
    this.heartbeatTimer = null
    this.pongTimer = null
    this.reconnectTimer = null
    this.reconnectAttempts = 0
    this.destroyed = false
    this.waitingPong = false
    this.lastMessageTime = 0
    this._hiddenAt = 0
    this.seenIds = new Set()
    this._chunkBuffer = {}
    this._rafId = null
    this._endedMessageIds = new Set()
    this._onVisibilityChange = this._handleVisibilityChange.bind(this)
    document.addEventListener('visibilitychange', this._onVisibilityChange)
  }

  // _chunkBuffer 复合 key：同会话不同话题并发流式时，按 conv+topic 隔离 buffer
  // 避免 chunks 混入错误话题（导致显示串联），也避免一个话题 reply_end 误删其他话题
  _chunkBufferKey(conversationId, topicId) {
    return conversationId + '::' + (topicId || 'default')
  }

  _flushChunks() {
    this._rafId = null
    for (const key of Object.keys(this._chunkBuffer)) {
      const buf = this._chunkBuffer[key]
      if (!buf.dirty) continue
      buf.dirty = false
      // 分别排序拼接
      buf.contentChunks.sort((a, b) => a.chunkIndex - b.chunkIndex)
      buf.thinkingChunks.sort((a, b) => a.chunkIndex - b.chunkIndex)
      let content = ''
      for (const c of buf.contentChunks) content += c.content
      let thinking = ''
      for (const c of buf.thinkingChunks) thinking += c.content
      const m = buf.meta
      this.store.commit('WS_AGENT_CHUNK', {
        conversationId: buf.conversationId,
        topicId: buf.topicId,
        content,
        thinking,
        isDelta: false,
        agentId: m.agentId,
        agentName: m.agentName || '',
        avatarUrl: m.avatarUrl || null,
        avatarColorRgb: m.avatarColorRgb || '',
        contentType: thinking && !content ? 'thinking' : 'text'
      })
    }
  }

  _scheduleChunk(conversationId, payload) {
    const key = this._chunkBufferKey(conversationId, payload.topicId)
    if (!this._chunkBuffer[key]) {
      this._chunkBuffer[key] = {
        conversationId,
        topicId: payload.topicId || null,
        contentChunks: [],
        thinkingChunks: [],
        meta: payload,
        dirty: false
      }
    }
    const buf = this._chunkBuffer[key]
    if (payload.contentType === 'thinking') {
      buf.thinkingChunks.push(payload)
    } else {
      buf.contentChunks.push(payload)
    }
    buf.dirty = true
    if (payload.agentName) buf.meta = payload
    if (!this._rafId) {
      this._rafId = requestAnimationFrame(() => this._flushChunks())
    }
  }

  connect() {
    const token = getToken()
    if (!token) return
    const deviceId = getDeviceId()
    const url = `${WS_URL}?token=${encodeURIComponent(token)}&device_id=${encodeURIComponent(deviceId)}`

    try {
      this.ws = new WebSocket(url)
    } catch (e) {
      console.warn('[ws] WebSocket 创建失败:', e.message)
      this._scheduleReconnect()
      return
    }

    this.ws.onopen = () => {
      console.log('[ws] 已连接')
      const isReconnect = this.reconnectAttempts > 0
      this.reconnectAttempts = 0
      this.lastMessageTime = Date.now()
      this._startHeartbeat()
      if (isReconnect) {
        console.log('[ws] 重连成功，刷新数据')
        this.store.dispatch('fetchConversations', { force: true })
        this.store.dispatch('fetchUnreadCount')
        var currentKey = this.store.state.currentSessionKey
        var currentItem = this.store.state.sessionItems.find(function(s) { return s.key === currentKey })
        if (currentItem && currentItem.conversationId) {
          this.store.dispatch('fetchConversationMessages', {
            conversationId: currentItem.conversationId,
            topicId: this.store.state.currentTopicKey || undefined
          })
        }
      }
    }

    this.ws.onmessage = (e) => {
      this.lastMessageTime = Date.now()
      if (this.waitingPong) {
        this.waitingPong = false
        if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null }
      }
      var raw = e.data
      try {
        const event = JSON.parse(raw)
        this._handleEvent(event)
      } catch (err) {
        console.warn('[ws] message parse error:', err.message, raw?.slice?.(0, 200))
      }
    }

    this.ws.onclose = () => {
      console.log('[ws] 连接断开')
      this._stopHeartbeat()
      if (!this.destroyed) this._scheduleReconnect()
    }

    this.ws.onerror = () => {
      this.ws && this.ws.close()
    }
  }

  _handleEvent(event) {
    const { type, id, data } = event
    if (!type || !data) return

    // 事件去重
    if (id) {
      if (this.seenIds.has(id)) return
      this.seenIds.add(id)
      if (this.seenIds.size > 1000) {
        const first = this.seenIds.values().next().value
        this.seenIds.delete(first)
      }
    }

    const store = this.store

    switch (type) {
      case 'pong':
        this.waitingPong = false
        if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null }
        break

      case 'ping':
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'pong' }))
        }
        break

      case 'message.new': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        const currentUserId = store.state.currentUserId

        // sender 对象优先，兼容旧版平铺字段
        const sender = data.sender || {}
        const senderType = sender.type || data.senderType || data.sender_type || 'user'
        const senderId = sender.id || sender.userId || data.senderId || data.sender_id || ''
        const senderName = sender.name || data.senderName || data.sender_name || ''
        const senderAvatar = sender.avatarUrl || null
        const senderColorRgb = sender.avatarColorRgb || ''

        const isMe = senderType !== 'agent' && !!(currentUserId && senderId && senderId === currentUserId)
        console.log('[ws debug isMe]', { senderId, senderIdType: typeof senderId, currentUserId, currentUserIdType: typeof currentUserId, strictEqual: senderId === currentUserId, isMe })
        const messageType = data.messageType || data.message_type || 'text'
        const body = data.body || {}

        // 头像展示：有图用图，无图取名字首字
        const avatarText = senderName ? senderName.slice(0, 1) : '?'
        // 是否 @了当前用户
        const mentions = data.mentions || []
        const isMentioned = !isMe && currentUserId && mentions.some(m => m.id === currentUserId || m.userId === currentUserId)

        const isAgent = senderType === 'agent'
        const isSystem = senderType === 'system' || senderId === 'system'

        let msg
        // 系统消息（群聊通知：进人、踢人、加 agent 等）
        if (isSystem) {
          msg = {
            id: data.id,
            seq: data.seq || 0,
            side: 'center',
            kind: 'system',
            text: body.text || data.text || '',
            time: _formatTime(data.createdAt || data.created_at),
            createdAt: data.createdAt || data.created_at || ''
          }
        } else if (messageType === 'file' || (body.files && body.files.length > 0)) {
          // messageType === 'file' 或 body.files 有内容时均按文件消息解析
          // 防御 Agent 回复时 messageType 字段缺失或值不标准的情况
          const files = body.files || []
          const firstFile = files[0] || {}
          const fileType = firstFile.type || (firstFile.mimeType && firstFile.mimeType.startsWith('image/') ? 'image' : 'file')
          // 多文件时用 multi，单文件按类型判断
          let kind
          if (files.length > 1) {
            kind = 'multi'
          } else if (fileType === 'image' || (firstFile.mimeType && firstFile.mimeType.startsWith('image/'))) {
            kind = 'image'
          } else if (fileType === 'video' || (firstFile.mimeType && firstFile.mimeType.startsWith('video/'))) {
            kind = 'video'
          } else if (fileType === 'audio' || (firstFile.mimeType && firstFile.mimeType.startsWith('audio/'))) {
            kind = 'audio'
          } else if (fileType === 'document' || fileType === 'markdown') {
            kind = 'document'
          } else {
            // 兜底：根据文件扩展名判断
            kind = _guessKindByExt(firstFile.fileName || firstFile.name) || 'file'
          }
          msg = {
            id: data.id,
            seq: data.seq || 0,
            readCount: 0,
            memberCount: 0,
            side: isMe ? 'right' : 'left',
            name: senderName,
            agentTag: isAgent ? 'Agent' : null,
            ownerName: isAgent ? (sender.ownerName || sender.owner_name || '') : '',
            text: (() => {
              let t = body.caption || body.text || ''
              if (t.includes('[@#')) {
                const allMembers = store.state.mentionableMembers[conversationId] || []
                t = t.replace(/\[@#(\d+)\]/g, (_, n) => {
                  const mn = mentions[parseInt(n, 10) - 1]
                  if (!mn) return '@'
                  if (mn.name) return '@' + mn.name
                  const found = allMembers.find(mb => (mb.id || mb.userId) === mn.id)
                  return found ? ('@' + (found.name || found.displayName || '')) : '@'
                })
              }
              return t
            })(),
            time: _formatTime(data.createdAt || data.created_at),
            createdAt: data.createdAt || data.created_at || '',
            avatarText,
            avatarImage: senderAvatar,
            avatarColorRgb: senderColorRgb,
            kind,
            imageUrl: kind === 'image' ? (firstFile.url || '') : '',
            fileUrl: firstFile.url || '',
            fileName: firstFile.fileName || firstFile.name || '',
            fileSize: firstFile.fileSize || firstFile.size || 0,
            fileSizeStr: _formatFileSize(firstFile.fileSize || firstFile.size || 0),
            mimeType: firstFile.mimeType || '',
            imageWidth: firstFile.width || 0,
            imageHeight: firstFile.height || 0,
            ext: (firstFile.fileName || firstFile.name || '').split('.').pop().toLowerCase(),
            files,
            rawText: body.rawText || body.raw_text || '',
            mentions: mentions,
            thinking: body.thinking || data.thinking || '',
            isMentioned: !!isMentioned
          }
        } else {
          const _replyTo = data.replyTo
          msg = {
            id: data.id,
            seq: data.seq || 0,
            readCount: 0,
            memberCount: 0,
            side: isMe ? 'right' : 'left',
            name: senderName,
            agentTag: isAgent ? 'Agent' : null,
            agentId: isAgent ? senderId : null,
            ownerName: isAgent ? (sender.ownerName || sender.owner_name || '') : '',
            text: body.text || data.text || '',
            rawText: body.rawText || body.raw_text || data.rawText || '',
            mentions: mentions,
            thinking: body.thinking || data.thinking || '',
            time: _formatTime(data.createdAt || data.created_at),
            createdAt: data.createdAt || data.created_at || '',
            avatarText,
            avatarImage: senderAvatar,
            avatarColorRgb: senderColorRgb,
            kind: 'text',
            isMentioned: !!isMentioned,
            replyTo: _replyTo ? (() => {
              let rText = _replyTo.text || ''
              if (rText.includes('[@#')) {
                // 从已加载消息里查找原始消息的已替换文本
                const convMsgs = store.state.conversationMessages[conversationId] || []
                const origMsg = convMsgs.find(m => m.id === _replyTo.id)
                if (origMsg && origMsg.text) {
                  rText = origMsg.text
                } else {
                  rText = rText.replace(/\[@#\d+\]/g, '@')
                }
              }
              return {
                id: _replyTo.id,
                name: (_replyTo.sender && _replyTo.sender.name) || '',
                text: rText,
                messageType: _replyTo.messageType || 'text',
                files: _replyTo.files || null,
                agentTag: (_replyTo.sender && _replyTo.sender.type === 'agent') ? 'Agent' : null
              }
            })() : null
          }
        }
        const topicId = data.topicId || data.topic_id || null
        store.commit('WS_NEW_MESSAGE', { conversationId, topicId, message: msg, senderType })
        // 实时更新 context_usage_display(多话题场景按 topicId 落到对应复合 key,
        // 跟 fetchConversationMessages 提取的复合 key 对齐,避免实时推送不更新)
        const _metadata = data.metadata || {}
        if (_metadata.context_usage_display) {
          store.commit('SET_CONTEXT_USAGE_DISPLAY', { conversationId, topicId, value: _metadata.context_usage_display })
        }
        // unreadStats：后端推送的各 topic 未读数，直接更新本地 topic 未读
        if (data.unreadStats && Array.isArray(data.unreadStats)) {
          const pageKey = `api-topics-${conversationId}`
          const page = store.state.sessionTopicPages[pageKey]
          if (page) {
            const updatedTopics = page.topics.map(t => {
              const stat = data.unreadStats.find(s => s.topicId === t.key)
              if (!stat) return t
              // 取本地和服务端的较大值，防止服务端旧值覆盖刚刚 WS_NEW_MESSAGE 的 +1
              return {
                ...t,
                unread: Math.max(Number(t.unread) || 0, stat.unreadCount || 0),
                mentionCount: Math.max(Number(t.mentionCount) || 0, stat.mentionCount || 0)
              }
            })
            store.commit('SET_SESSION_TOPIC_PAGE', { pageKey, page: { ...page, topics: updatedTopics } })
          }
        }
        // 如果收到的消息属于当前正在浏览的会话+话题，立即标记已读
        {
          const currentItem = store.state.sessionItems.find(
            s => s.key === store.state.currentSessionKey
          )
          if (currentItem && currentItem.conversationId === conversationId) {
            const viewingTopicId = store.state.currentTopicKey
            // 只有消息所属话题与当前查看话题一致（或无话题）才标记已读，避免其他话题的消息触发已读
            if (viewingTopicId && topicId && viewingTopicId !== topicId) {
              // 不同话题，不标记已读
            } else {
              const activeTopicId = viewingTopicId || topicId
              if (activeTopicId) store.dispatch('markTopicRead', activeTopicId)
            }
          }
        }
        // 收到新消息后防抖校准 totalUnread（本地已即时 +1，延迟用服务端值校准）
        if (!isMe) {
          if (this._unreadCountTimer) clearTimeout(this._unreadCountTimer)
          this._unreadCountTimer = setTimeout(() => {
            store.dispatch('fetchUnreadCount')
            this._unreadCountTimer = null
          }, 3000)
        }
        // 浏览器桌面通知：非自己的消息 + 非当前活跃会话或页面不可见
        // 只有 currentUserId 已加载时才能可靠判断 isMe，未加载时不弹（防止把自己的消息也通知出来）
        if (!isMe && currentUserId) {
          _triggerBrowserNotification(store, {
            title: senderName || 'New Message',
            body: body.text || (body.files ? '[文件]' : ''),
            icon: senderAvatar,
            conversationId
          })
        }
        break
      }

      case 'agent.reply_start': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        // 清除该会话可能残留的 pending chunks 和 reply_end 标记
        delete this._pendingChunks[conversationId]
        store.commit('WS_AGENT_TYPING', {
          conversationId,
          topicId: data.topicId || data.topic_id || null,
          agentId: data.agentId,
          agentName: data.agentName,
          avatarUrl: data.avatarUrl || null,
          avatarColorRgb: data.avatarColorRgb || ''
        })
        // 更新 sessionItem 的 myAgentStatus 为 thinking —— 仅 1对1(群聊靠 agent.status_changed 的聚合值, 不被单 agent 流式覆盖)
        {
          const idx = store.state.sessionItems.findIndex(s => s.conversationId === conversationId)
          if (idx >= 0 && store.state.sessionItems[idx].type === 'agent') {
            store.commit('UPDATE_SESSION_ITEM', { key: store.state.sessionItems[idx].key, updates: { myAgentStatus: 'thinking' } })
          }
        }
        break
      }

      case 'agent.reply_chunk': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        // 忽略已结束的 messageId 的迟到 chunks
        const chunkMsgId = data.messageId
        if (chunkMsgId && this._endedMessageIds.has(chunkMsgId)) break
        this._scheduleChunk(conversationId, {
          chunkIndex: data.chunkIndex || 1,
          content: data.content || '',
          isDelta: data.isDelta !== false,
          agentId: data.agentId,
          agentName: data.agentName || '',
          avatarUrl: data.avatarUrl || null,
          avatarColorRgb: data.avatarColorRgb || '',
          contentType: 'thinking',
          topicId: data.topicId || data.topic_id || null
        })
        break
      }

      case 'agent.content_chunk': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        const chunkMsgId = data.messageId
        if (chunkMsgId && this._endedMessageIds.has(chunkMsgId)) break
        this._scheduleChunk(conversationId, {
          chunkIndex: data.chunkIndex || 1,
          content: data.content || '',
          isDelta: data.isDelta !== false,
          agentId: data.agentId,
          agentName: data.agentName || '',
          avatarUrl: data.avatarUrl || null,
          avatarColorRgb: data.avatarColorRgb || '',
          contentType: 'text',
          topicId: data.topicId || data.topic_id || null
        })
        break
      }

      case 'agent.content_end': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        // 不删 _chunkBuffer 不加 _endedMessageIds，避免影响思考流式
        store.commit('WS_AGENT_CONTENT_DONE', {
          conversationId,
          topicId: data.topicId || data.topic_id || null,
          messageId: data.messageId,
          content: data.content || '',
          agentName: data.agentName,
          agentId: data.agentId,
          avatarUrl: data.avatarUrl || null,
          avatarColorRgb: data.avatarColorRgb || ''
        })
        // myAgentStatus → idle —— 仅 1对1(群聊靠 agent.status_changed 的聚合值)
        {
          const idx = store.state.sessionItems.findIndex(s => s.conversationId === conversationId)
          if (idx >= 0 && store.state.sessionItems[idx].type === 'agent') {
            store.commit('UPDATE_SESSION_ITEM', { key: store.state.sessionItems[idx].key, updates: { myAgentStatus: 'idle' } })
          }
        }
        // 当前活跃会话标记已读
        {
          const currentItem = store.state.sessionItems.find(s => s.key === store.state.currentSessionKey)
          if (currentItem && currentItem.conversationId === conversationId) {
            const activeTopicId = store.state.currentTopicKey || (data.topicId || data.topic_id)
            if (activeTopicId) store.dispatch('markTopicRead', activeTopicId)
          }
        }
        // 注意：浏览器通知统一由 message.new 触发，这里不再重复弹
        break
      }

      case 'agent.group_plan': {
        const conversationId = data.conversationId || data.conversation_id
        const topicId = data.topicId || data.topic_id
        const agentId = data.agentId
        if (!conversationId || !topicId || !agentId) break
        store.commit('SET_GROUP_PLAN', {
          conversationId,
          topicId,
          agentId,
          plan: {
            agentId,
            agentName: data.agentName || '',
            avatarUrl: data.avatarUrl || null,
            metadata: data.metadata || {}
          }
        })
        break
      }

      case 'agent.reply_end': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        // 只清这个话题的 buffer，不影响同会话其他话题正在进行的流式
        const _endTopicId = data.topicId || data.topic_id || null
        delete this._chunkBuffer[this._chunkBufferKey(conversationId, _endTopicId)]
        // 记录已结束的 messageId，忽略迟到的 chunks
        if (data.messageId) {
          this._endedMessageIds.add(data.messageId)
          // 防止无限增长，超过 100 条时清理最早的
          if (this._endedMessageIds.size > 100) {
            const iter = this._endedMessageIds.values()
            for (let i = 0; i < 50; i++) this._endedMessageIds.delete(iter.next().value)
          }
        }
        store.commit('WS_AGENT_DONE', {
          conversationId,
          topicId: data.topicId || data.topic_id || null,
          messageId: data.messageId,
          content: data.content || '',
          thinking: data.thinking || '',
          agentName: data.agentName,
          agentId: data.agentId,
          avatarUrl: data.avatarUrl || null
        })
        // 更新 sessionItem 的 myAgentStatus 回 idle —— 仅 1对1(群聊靠 agent.status_changed 的聚合值)
        {
          const idx = store.state.sessionItems.findIndex(s => s.conversationId === conversationId)
          if (idx >= 0 && store.state.sessionItems[idx].type === 'agent') {
            store.commit('UPDATE_SESSION_ITEM', { key: store.state.sessionItems[idx].key, updates: { myAgentStatus: 'idle' } })
          }
        }
        // 如果是当前活跃会话且 agent 回复的就是用户正在看的话题，才标记已读
        {
          const currentItem = store.state.sessionItems.find(
            s => s.key === store.state.currentSessionKey
          )
          const replyTopicId = data.topicId || data.topic_id
          if (currentItem && currentItem.conversationId === conversationId) {
            if (store.state.currentTopicKey && store.state.currentTopicKey === replyTopicId) {
              store.dispatch('markTopicRead', replyTopicId)
            }
          }
        }
        // 注意：浏览器通知统一由 message.new 触发，这里不再重复弹
        break
      }

      case 'agent.status_changed': {
        // Agent 工作状态变更。后端两套语义同事件:
        //   - newStatus: per-agent 原始细粒度态(thinking/executing_tool/idle/...),给聊天等 per-agent 逻辑
        //   - myAgentStatus: 该事件 agent 的 owner 在本会话的【聚合】粗粒度态(busy/idle/disabled),给会话列表圆点
        // 会话列表圆点只读聚合值, 且仅当 ownerId===当前用户(过滤掉别人 agent 触发的事件)
        const conversationId = data.conversationId || data.conversation_id
        const ownerId = data.ownerId || data.owner_id
        const me = store.state.currentUserId
        const agg = data.myAgentStatus  // 新: 聚合值
        if (conversationId && agg && ownerId && me && ownerId === me) {
          const idx = store.state.sessionItems.findIndex(s => s.conversationId === conversationId)
          if (idx >= 0) {
            store.commit('UPDATE_SESSION_ITEM', { key: store.state.sessionItems[idx].key, updates: { myAgentStatus: agg } })
          }
          break
        }
        // 向后兼容(后端未带 myAgentStatus/ownerId 时): 退回 per-agent newStatus, 仅 1对1 用
        // (群聊不能用单个 agent 原始态当整会话状态, 没聚合值就不动, 等后端上线)
        const status = data.newStatus || data.status
        if (!status) break
        if (conversationId) {
          const idx = store.state.sessionItems.findIndex(s => s.conversationId === conversationId)
          if (idx >= 0 && store.state.sessionItems[idx].type === 'agent') {
            store.commit('UPDATE_SESSION_ITEM', { key: store.state.sessionItems[idx].key, updates: { myAgentStatus: status } })
          }
        } else {
          const agentId = data.agentId || data.agent_id
          if (agentId) {
            store.state.sessionItems.forEach((item) => {
              if (item.type === 'agent') {
                store.commit('UPDATE_SESSION_ITEM', { key: item.key, updates: { myAgentStatus: status } })
              }
            })
          }
        }
        break
      }

      case 'agent.event': {
        if (data.action === 'change_model' && data.data) {
          const conversationId = data.conversationId || data.conversation_id
          const currentItem = store.state.sessionItems.find(s => s.key === store.state.currentSessionKey)
          if (currentItem && currentItem.conversationId === conversationId) {
            if (data.data.model) store.commit('SET_SELECTED_MODEL', data.data.model)
            if (data.data.think) store.commit('SET_THINKING_DEPTH', data.data.think)
          }
        }
        break
      }

      case 'agent.updated': {
        const agentId = data.agentId
        if (!agentId) break
        const newName = data.agentName || ''
        const newAvatar = data.avatarUrl || null
        // 更新会话列表中对应 Agent 会话的名称和头像
        for (let i = 0; i < store.state.sessionItems.length; i++) {
          const s = store.state.sessionItems[i]
          if (s.type === 'agent' && s.name) {
            // agent 私聊：通过 conversationAgents 匹配
            const agents = store.state.conversationAgents[s.conversationId] || []
            if (agents.some(a => a.id === agentId)) {
              store.commit('UPDATE_SESSION_ITEM', {
                key: s.key,
                updates: {
                  name: newName || s.name,
                  avatarUrl: newAvatar !== null ? newAvatar : s.avatarUrl
                }
              })
            }
          }
        }
        // 更新 agentSessionProfiles
        for (const [key, profile] of Object.entries(store.state.agentSessionProfiles || {})) {
          if (profile && profile.agentId === agentId) {
            store.state.agentSessionProfiles[key] = {
              ...profile,
              name: newName || profile.name,
              avatarUrl: newAvatar !== null ? newAvatar : profile.avatarUrl
            }
          }
        }
        break
      }

      case 'agent.disabled': {
        // 2026-05-28 新增: 用户主动停用 Agent (DELETE /agents/:id 改为停用语义). 推送给 owner 多端 + 所在所有会话参与者.
        // data: { agentId, agentName, ownerId, ownerName, disabledAt }
        const agentId = data.agentId || data.agent_id
        if (!agentId) break
        const disabledAt = data.disabledAt || data.disabled_at || new Date().toISOString()
        // 1) 关联会话:
        //    - conversationAgents[].status → 'disabled' (聊天气泡徽标 + 群聊聚合状态都查这里)
        //    - 仅 1对1 (type==='agent') 时才改 sessionItem.myAgentStatus
        //      群聊不改, 因为我可能有多个 agent, 别人停用 agent 也不该污染我的会话项
        //      (SessionsBar 的 aggregateMyAgentStatus 会自动从 conversationAgents 重新聚合)
        for (let i = 0; i < store.state.sessionItems.length; i++) {
          const s = store.state.sessionItems[i]
          const agents = store.state.conversationAgents[s.conversationId] || []
          const hit = agents.some(a => a.id === agentId)
          if (hit) {
            const patched = agents.map(a => a.id === agentId ? { ...a, status: 'disabled' } : a)
            store.commit('SET_CONVERSATION_AGENTS', { conversationId: s.conversationId, agents: patched })
            if (s.type === 'agent') {
              store.commit('UPDATE_SESSION_ITEM', { key: s.key, updates: { myAgentStatus: 'disabled' } })
            }
          }
        }
        // 2) 留个 tick 给 WorkspaceView watch (局部更新 agentItems 灰显沉底 + 详情若开着就关)
        store.commit('WS_AGENT_DISABLED', {
          agentId,
          agentName: data.agentName || data.agent_name || '',
          ownerId: data.ownerId || data.owner_id || '',
          ownerName: data.ownerName || data.owner_name || '',
          disabledAt
        })
        break
      }

      case 'message.read': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        const lastReadSeq = data.lastReadSeq || data.last_read_seq || 0
        const readUserId = data.userId || data.user_id || ''
        const topicId = data.topicId || data.topic_id || null
        const readAt = data.readAt || data.read_at || data.lastReadAt || data.last_read_at || data.timestamp || null
        const currentUserId = store.state.currentUserId
        const isSelf = currentUserId && readUserId === currentUserId
        // 更新消息级别的已读状态（readCount +1）— 不处理自己的读取，自己的消息不能被自己已读
        if (readUserId && !isSelf) {
          store.commit('WS_MESSAGE_READ', { conversationId, lastReadSeq, userId: readUserId, readAt })
          // 仅当本地有 seq=0 的右侧消息时，才防抖重拉（修复 seq=0 无法匹配 lastReadSeq 的问题）
          // 避免每次 message.read 都重拉消息列表导致其他设备频繁请求
          const localMsgs = store.state.conversationMessages[conversationId] || []
          const hasSeq0 = localMsgs.some(m => m.side === 'right' && (m.seq === 0 || !m.seq))
          if (hasSeq0) {
            if (this._readRefetchTimers && this._readRefetchTimers[conversationId]) {
              clearTimeout(this._readRefetchTimers[conversationId])
            }
            if (!this._readRefetchTimers) this._readRefetchTimers = {}
            this._readRefetchTimers[conversationId] = setTimeout(() => {
              const currentItem = store.state.sessionItems.find(s => s.key === store.state.currentSessionKey)
              if (currentItem && currentItem.conversationId === conversationId) {
                store.dispatch('fetchConversationMessages', { conversationId, topicId: store.state.currentTopicKey || undefined })
              }
              delete this._readRefetchTimers[conversationId]
            }, 800)
          }
        }
        // 多端同步：如果是当前用户在另一台设备上的已读，清掉对应的 unread badge
        if (isSelf) {
          if (topicId) {
            // 有 topicId：只清该话题的 unread，然后从话题汇总到会话
            for (const pageKey of Object.keys(store.state.sessionTopicPages)) {
              const page = store.state.sessionTopicPages[pageKey]
              const tIdx = page.topics.findIndex(t => t.key === topicId)
              if (tIdx >= 0) {
                const updatedTopics = page.topics.map(t => t.key === topicId ? { ...t, unread: 0, mentionCount: 0 } : t)
                store.commit('SET_SESSION_TOPIC_PAGE', { pageKey, page: { ...page, topics: updatedTopics } })
                const totalUnread = updatedTopics.reduce((sum, t) => sum + (Number(t.unread) || 0), 0)
                const totalMention = updatedTopics.reduce((sum, t) => sum + (Number(t.mentionCount) || 0), 0)
                const session = store.state.sessionItems.find(s => s.key === page.sessionKey)
                if (session) {
                  store.commit('UPDATE_SESSION_ITEM', { key: session.key, updates: { unread: totalUnread, mentionCount: totalMention } })
                }
                break
              }
            }
          } else {
            // 无 topicId：检查该会话是否有话题分页
            const sIdx = store.state.sessionItems.findIndex(s => s.conversationId === conversationId)
            if (sIdx >= 0) {
              const si = store.state.sessionItems[sIdx]
              const page = si.topicKey && store.state.sessionTopicPages[si.topicKey]
              if (page && page.topics.length > 0) {
                // 有话题的群：从话题汇总，不直接清零（其他话题可能有未读）
                const totalUnread = page.topics.reduce((sum, t) => sum + (Number(t.unread) || 0), 0)
                const totalMention = page.topics.reduce((sum, t) => sum + (Number(t.mentionCount) || 0), 0)
                store.commit('UPDATE_SESSION_ITEM', { key: si.key, updates: { unread: totalUnread, mentionCount: totalMention } })
              } else {
                // 无话题的普通会话：直接清零
                store.commit('UPDATE_SESSION_ITEM', { key: si.key, updates: { unread: 0, mentionCount: 0 } })
              }
            }
          }
          // 重新拉一次 unread-count，确保 totalUnread 等其他状态同步
          store.dispatch('fetchUnreadCount')
        } else {
          // 非自己读的：仅当当前活跃会话且无多话题时清 badge
          const currentItem = store.state.sessionItems.find(s => s.key === store.state.currentSessionKey)
          if (currentItem && currentItem.conversationId === conversationId) {
            const page = currentItem.topicKey && store.state.sessionTopicPages[currentItem.topicKey]
            if (page && page.topics.length > 1) {
              // 多话题群：从话题汇总，不直接清零
              const totalUnread = page.topics.reduce((sum, t) => sum + (Number(t.unread) || 0), 0)
              const totalMention = page.topics.reduce((sum, t) => sum + (Number(t.mentionCount) || 0), 0)
              store.commit('UPDATE_SESSION_ITEM', { key: currentItem.key, updates: { unread: totalUnread, mentionCount: totalMention } })
            } else {
              store.commit('UPDATE_SESSION_ITEM', { key: currentItem.key, updates: { unread: 0 } })
            }
          }
        }
        break
      }

      case 'message.pinned': {
        const convId = data.conversationId || data.conversation_id
        const messageId = data.messageId || data.message_id
        if (!convId || !messageId) break
        const topicId = data.topicId || data.topic_id || null
        const key = `${convId}::${topicId || 'default'}`
        store.commit('ADD_PINNED_MESSAGE', { key, item: {
          id: data.pinId || data.pin_id || ('pin-' + messageId),
          messageId,
          topicId,
          pinnedBy: data.pinnedBy || data.pinned_by || '',
          pinnedByName: data.pinnedByName || data.pinned_by_name || '',
          pinnedAt: data.pinnedAt || data.pinned_at || new Date().toISOString(),
          message: data.message || null
        }})
        // 新置顶时让被 dismiss 的 bar 重新可见
        store.commit('SET_PINNED_BAR_DISMISSED', { key, dismissed: false })
        break
      }

      case 'message.unpinned': {
        const convId = data.conversationId || data.conversation_id
        const messageId = data.messageId || data.message_id
        if (!convId || !messageId) break
        const topicId = data.topicId || data.topic_id || null
        const key = `${convId}::${topicId || 'default'}`
        store.commit('REMOVE_PINNED_MESSAGE', { key, messageId })
        break
      }

      case 'conversation.created': {
        // 直接用推送数据 append 到会话列表（数据结构与 GET /conversations 一致）
        const convId = data.id
        if (!convId) break
        // 已存在则跳过（防重复）
        if (store.state.sessionItems.some(s => s.conversationId === convId)) break
        // 复用 fetchConversations 的映射逻辑，commit 一个新的 session
        store.commit('APPEND_SESSION_FROM_WS', data)
        break
      }

      case 'conversation.deleted': {
        const conversationId = data.id || data.conversationId || data.conversation_id
        if (!conversationId) break
        const delIdx = store.state.sessionItems.findIndex(s => s.conversationId === conversationId)
        if (delIdx >= 0) {
          const delItem = store.state.sessionItems[delIdx]
          // 如果正在查看该会话，先切走再移除
          if (store.state.currentSessionKey === delItem.key) {
            const other = store.state.sessionItems.find(s => s.key !== delItem.key)
            if (other) {
              store.dispatch('setActiveSession', other.key)
            } else {
              store.state.currentSessionKey = null
              store.state.currentSessionType = 'agent'
            }
          }
          store.state.sessionItems.splice(delIdx, 1)
        }
        break
      }

      // ── 成员变更事件 ──────────────────────────────────────

      case 'conversation.member_joined': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        store.dispatch('fetchMentionableMembers', conversationId)
        store.dispatch('fetchAvailableMembers', conversationId)
        store.commit('INCREMENT_ONLINE_COUNT_VERSION')
        // 更新成员数量（如果后端直接推送了）
        {
          const item = store.state.sessionItems.find(s => s.conversationId === conversationId)
          if (item) {
            const updates = {}
            if (data.memberCount != null) updates.memberCount = data.memberCount
            if (data.agentCount != null) updates.agentCount = data.agentCount
            if (data.totalCount != null) updates.totalCount = data.totalCount
            if (Object.keys(updates).length) store.commit('UPDATE_SESSION_ITEM', { key: item.key, updates })
          }
        }
        // 如果新成员就是自己，重新拉取会话列表
        if (data.userId && data.userId === store.state.currentUserId) {
          store.dispatch('fetchConversations', { force: true })
        }
        break
      }

      case 'conversation.member_left': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        store.dispatch('fetchMentionableMembers', conversationId)
        store.dispatch('fetchAvailableMembers', conversationId)
        store.commit('INCREMENT_ONLINE_COUNT_VERSION')
        // 更新成员数量
        {
          const item = store.state.sessionItems.find(s => s.conversationId === conversationId)
          if (item) {
            const updates = {}
            if (data.memberCount != null) updates.memberCount = data.memberCount
            if (data.agentCount != null) updates.agentCount = data.agentCount
            if (data.totalCount != null) updates.totalCount = data.totalCount
            if (Object.keys(updates).length) store.commit('UPDATE_SESSION_ITEM', { key: item.key, updates })
          }
        }
        // 群主退群可能触发管理员转移，刷新会话列表以更新每个人的 isAdmin
        store.dispatch('fetchConversations', { force: true })
        // 如果是自己被踢
        if (data.userId && data.userId === store.state.currentUserId) {
          const item = store.state.sessionItems.find(s => s.conversationId === conversationId)
          if (item) store.commit('UPDATE_SESSION_ITEM', { key: item.key, updates: { exited: true } })
          if (store.state.currentSessionKey === (item && item.key)) {
            store.commit('SET_CURRENT_SESSION', { key: null, type: 'agent' })
          }
          // 被踢后立即刷新账号级总未读，否则该群的未读会一直挂在 totalUnread 上，等轮询才消失
          store.dispatch('fetchUnreadCount')
        }
        break
      }

      case 'conversation.agent_added': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        store.dispatch('fetchConversationAgents', conversationId)
        store.dispatch('fetchMentionableMembers', conversationId)
        store.dispatch('fetchAvailableMembers', conversationId)
        {
          const item = store.state.sessionItems.find(s => s.conversationId === conversationId)
          if (item) {
            const updates = { hasAgent: true }
            if (data.memberCount != null) updates.memberCount = data.memberCount
            if (data.agentCount != null) updates.agentCount = data.agentCount
            if (data.totalCount != null) updates.totalCount = data.totalCount
            // 如果是自己的 agent 被加入
            const myAgentId = store.state.currentUserId
            if (data.agentId && data.agentOwner === myAgentId) {
              updates.hasMyAgent = true
              updates.myAgentStatus = 'idle'
            }
            store.commit('UPDATE_SESSION_ITEM', { key: item.key, updates })
          }
        }
        break
      }

      case 'conversation.agent_removed': {
        const conversationId = data.conversationId || data.conversation_id
        if (!conversationId) break
        store.dispatch('fetchConversationAgents', conversationId)
        store.dispatch('fetchMentionableMembers', conversationId)
        store.dispatch('fetchAvailableMembers', conversationId)
        {
          const item = store.state.sessionItems.find(s => s.conversationId === conversationId)
          if (item) {
            const updates = {}
            if (data.memberCount != null) updates.memberCount = data.memberCount
            if (data.agentCount != null) updates.agentCount = data.agentCount
            if (data.totalCount != null) updates.totalCount = data.totalCount
            // agentCount 降为 0 时清除 hasAgent 标签
            const newAgentCount = data.agentCount != null ? data.agentCount : (item.agentCount || 1) - 1
            if (newAgentCount <= 0) updates.hasAgent = false
            // 如果是自己的 agent 被移除
            const myAgentId = store.state.currentUserId
            if (data.agentId && data.agentOwner === myAgentId) {
              updates.hasMyAgent = false
              updates.myAgentStatus = null
            }
            store.commit('UPDATE_SESSION_ITEM', { key: item.key, updates })
          }
        }
        break
      }

      case 'user.status_changed': {
        // 用户在线状态变更，触发当前会话在线人数刷新
        store.commit('INCREMENT_ONLINE_COUNT_VERSION')
        break
      }

      case 'conversation.updated': {
        // 会话更新（改名、设置等），用接口返回的数据直接更新本地
        const convId = data.id || data.conversationId || data.conversation_id
        if (convId) {
          const updates = {}
          if (data.name) updates.name = data.name
          if (data.avatar !== undefined) updates.avatar = data.avatar
          if (data.pinned !== undefined) updates.pinned = data.pinned
          if (Object.keys(updates).length) {
            store.commit('UPDATE_SESSION_ITEM', { key: convId, updates })
          }
        }
        break
      }

      case 'topic.created': {
        const conversationId = data.conversation_id || data.conversationId
        if (!conversationId) break
        store.commit('APPEND_TOPIC_FROM_WS', { conversationId, topic: data })
        break
      }

      case 'topic.updated': {
        // 话题更新（改名、置顶等）：用 payload 就地更新对应话题，无需全量重拉
        // payload: { topicId, conversationId, name?, status?, pinned?, updatedBy, updatedAt }（字段 omitempty）
        const conversationId = data.conversationId || data.conversation_id
        const topicId = data.topicId || data.topic_id
        if (conversationId && topicId) {
          store.commit('UPDATE_TOPIC_FROM_WS', {
            conversationId,
            topicId,
            name: data.name,
            pinned: data.pinned,
            status: data.status
          })
        } else {
          // 缺关键字段时兜底：仍重拉一次，保证不漏更新
          store.dispatch('fetchConversations', { force: true })
        }
        break
      }

      case 'topic.closed': {
        // 话题被关闭/删除，重新拉取会话列表刷新话题数据
        const conversationId = data.conversationId || data.conversation_id
        const topicId = data.topicId || data.topic_id
        store.dispatch('fetchConversations', { force: true }).then(() => {
          const session = store.state.sessionItems.find(s => s.conversationId === conversationId)
          if (session) {
            const page = session.topicKey ? store.state.sessionTopicPages[session.topicKey] : null
            const topicCount = page ? page.topics.length : 0
            // 如果话题数 <= 1，关闭话题视图
            if (!session.hasTopics || topicCount <= 1) {
              store.commit('SET_TOPICS_VIEW', { open: false })
            }
            // 如果当前正在查看被关闭的话题，切到剩余第一个
            if (store.state.currentTopicKey === topicId && page && page.topics.length) {
              store.dispatch('switchTopic', { topicKey: page.topics[0].key, pageKey: session.topicKey })
            }
          }
        })
        break
      }

      // 知识库权限变更（成员加/减/角色改/share accept）
      // payload: { spaceId, userId, reason: 'added'|'removed'|'role_changed'|'share_accepted' }
      // KnowledgeView 监听这个 window event，命中当前 iframe 的 spaceId 时强制 reload iframe
      case 'wiki.perm_changed': {
        try {
          window.dispatchEvent(new CustomEvent('wiki-perm-changed', { detail: data || {} }))
        } catch (_) {}
        break
      }

      default:
        break
    }
  }

  _startHeartbeat() {
    this._stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        var sinceLastMsg = Date.now() - this.lastMessageTime
        if (sinceLastMsg < HEARTBEAT_INTERVAL) return
        this.waitingPong = true
        this.ws.send(JSON.stringify({ type: 'ping' }))
        this.pongTimer = setTimeout(() => {
          if (this.waitingPong) {
            console.warn('[ws] pong 超时，主动断开重连')
            this.waitingPong = false
            if (this.ws) this.ws.close()
          }
        }, PONG_TIMEOUT)
      }
    }, HEARTBEAT_INTERVAL)
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null }
    if (this.pongTimer) { clearTimeout(this.pongTimer); this.pongTimer = null }
    this.waitingPong = false
  }

  _scheduleReconnect() {
    if (this.reconnectTimer) return
    const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts), MAX_RECONNECT_DELAY)
    this.reconnectAttempts++
    console.log(`[ws] ${delay}ms 后重连 (第${this.reconnectAttempts}次)`)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (!this.destroyed) this.connect()
    }, delay)
  }

  _handleVisibilityChange() {
    if (this.destroyed) return
    if (document.visibilityState === 'hidden') {
      this._hiddenAt = Date.now()
      return
    }
    var hiddenDuration = this._hiddenAt ? Date.now() - this._hiddenAt : 0
    this._hiddenAt = 0

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.log('[ws] 页面恢复可见，连接已断开，立即重连')
      this.reconnectAttempts = 0
      if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
      this.connect()
    }

    if (hiddenDuration > 30000) {
      console.log('[ws] 后台 ' + Math.round(hiddenDuration / 1000) + 's，刷新数据')
      this.store.dispatch('fetchConversations', { force: true })
      this.store.dispatch('fetchUnreadCount')
      var currentKey = this.store.state.currentSessionKey
      var currentItem = this.store.state.sessionItems.find(function(s) { return s.key === currentKey })
      if (currentItem && currentItem.conversationId) {
        this.store.dispatch('fetchConversationMessages', {
          conversationId: currentItem.conversationId,
          topicId: this.store.state.currentTopicKey || undefined
        })
      }
    }
  }

  destroy() {
    this.destroyed = true
    document.removeEventListener('visibilitychange', this._onVisibilityChange)
    this._stopHeartbeat()
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null }
    this._chunkBuffer = {}
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null }
    if (this.ws) {
      this.ws.onclose = null
      this.ws.close()
    }
  }
}

function _formatTime(isoStr) {
  if (!isoStr) return ''
  try {
    const d = new Date(isoStr)
    return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch { return '' }
}

// 与 store/index.js 的 formatFileSize 同口径：0/空 → ''，<1MB → KB，否则 MB
function _formatFileSize(bytes) {
  if (!bytes || bytes === 0) return ''
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

let _client = null

export function initWs(store) {
  if (_client) _client.destroy()
  _client = new WebSocketClient(store)
  _client.connect()
}

export function destroyWs() {
  if (_client) {
    _client.destroy()
    _client = null
  }
}
